import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { adminApi } from '@/api/workspaces'
import { RefreshFailed } from '@/components/RefreshFailed'
import { adminCopy } from '@/copy'
import type { LogEntry, LogLevel } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Input, Select } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SearchInput } from '@/ui/SearchInput/SearchInput'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { formatClockPrecise } from '../../utils/time'
import styles from './LogsTab.module.css'

const LEVEL_FILTERS: Array<LogLevel | ''> = [
  '',
  'error',
  'warn',
  'info',
  'debug',
  'trace',
]

const SEARCH_DEBOUNCE_MS = 250

function summarizeTarget(t: string): string {
  // Strip the crate prefix to keep the column scannable: the user already
  // knows they're looking at chromaframe logs.
  return t
    .replace(/^chromaframe(_session|_core|_tools|_runtime|_providers)?::/, '')
    .replace(/^crate::/, '')
}

function fieldPairs(fields: Record<string, unknown> | null): string[] {
  if (!fields) return []
  return Object.entries(fields).map(
    ([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`,
  )
}

function formatLine(e: LogEntry): string {
  return [
    formatClockPrecise(e.ts_ms),
    e.level.toUpperCase().padEnd(5),
    summarizeTarget(e.target).padEnd(16),
    e.message,
    ...fieldPairs(e.fields),
    e.request_id && `request_id=${e.request_id}`,
    e.spans.length > 0 && `spans=${e.spans.join(' › ')}`,
  ]
    .filter(Boolean)
    .join('  ')
}

export function LogsTab() {
  const [level, setLevel] = useState<LogLevel | ''>('')
  const [requestId, setRequestId] = useState('')
  // The field follows every keystroke; the query follows it 250 ms later.
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  // When set, the coordinator proxies the logs query to this workspace's
  // worker. `null` = read the local (coordinator) buffer. Hidden in
  // standalone mode (no workers to proxy to).
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchDraft), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchDraft])

  // One-time fetch of system status to (a) learn whether we're in
  // coordinator mode and (b) enumerate the workers we can proxy to. In
  // standalone mode the selector stays empty and the proxy param is never
  // sent. Cached for the tab's lifetime — workers come and go but a manual
  // refresh via tab-switch picks up new ones.
  const { data: status } = useQuery({
    queryKey: ['admin', 'status'],
    queryFn: () => adminApi.systemStatus(),
    staleTime: 60_000,
  })
  const isCoordinator = status?.mode === 'coordinator'
  const workers = status?.workers ?? []

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'logs', level, requestId, search, workspaceId],
    queryFn: () =>
      adminApi.recentLogs({
        limit: 500,
        level: level || undefined,
        request_id: requestId || undefined,
        q: search || undefined,
        workspace_id: workspaceId ?? undefined,
      }),
    refetchInterval: 3000,
    // Cached entries are immutable; react-query can keep previous data while
    // refetching to avoid flicker.
    placeholderData: (prev) => prev,
  })
  const showSkeleton = useSkeletonDelay(isLoading)

  const entries = data?.entries ?? []

  function clearFilters() {
    setLevel('')
    setRequestId('')
    setSearchDraft('')
    setSearch('')
  }

  const retry = (
    <Button variant="outline" size="sm" onClick={() => void refetch()}>
      {adminCopy.retry}
    </Button>
  )

  let body: ReactNode
  if (isError && !data) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline" wrap>
          <Text>{adminCopy.loadFailed.logs}</Text>
          {retry}
        </Inline>
      </Alert>
    )
  } else if (isLoading) {
    body = showSkeleton ? <Skeleton lines={6} /> : null
  } else if (entries.length === 0) {
    const filtered = Boolean(level || requestId || search)
    body = (
      <EmptyState
        variant="inline"
        description={adminCopy.logs.empty(level, requestId, search)}
        action={
          filtered ? (
            <Button variant="outline" onClick={clearFilters}>
              {adminCopy.logs.clearFilters}
            </Button>
          ) : undefined
        }
      />
    )
  } else {
    body = (
      <CodeBlock
        title={
          workspaceId
            ? adminCopy.logs.workerTitle(workspaceId)
            : (status?.mode ?? adminCopy.logs.localBuffer)
        }
        caption={adminCopy.logs.caption}
      >
        {entries.map(formatLine).join('\n')}
      </CodeBlock>
    )
  }

  return (
    <section className={styles.root}>
      <SectionHeader rule trailingSpacing="section">
        {adminCopy.logs.heading}
      </SectionHeader>
      <Stack gap="space-12">
        {/* D223: the filters' labels are visually hidden (D8) and each box's
            value or placeholder is its visible hint; the row aligns on the
            boxes' tops so the search's count hangs below it. */}
        <Inline gap="space-8" align="start">
          <div className={styles.level}>
            <Select
              aria-label={adminCopy.logs.levelLabel}
              value={level}
              onChange={(e) => setLevel(e.target.value as LogLevel | '')}
            >
              {LEVEL_FILTERS.map((l) => (
                <option key={l || 'all'} value={l}>
                  {l || adminCopy.logs.allLevels}
                </option>
              ))}
            </Select>
          </div>
          {isCoordinator && (
            <div className={styles.buffer}>
              <Select
                aria-label={adminCopy.logs.bufferLabel}
                value={workspaceId ?? ''}
                onChange={(e) => setWorkspaceId(e.target.value || null)}
              >
                <option value="">{adminCopy.logs.coordinator}</option>
                {workers.map((w) => (
                  <option key={w.id} value={w.workspace_id}>
                    {adminCopy.logs.workerOption(w.workspace_id)}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className={styles.request}>
            <Input
              mono
              aria-label={adminCopy.logs.requestLabel}
              placeholder={adminCopy.logs.requestPlaceholder}
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
            />
          </div>
          <SearchInput
            label={adminCopy.logs.searchLabel}
            placeholder={adminCopy.logs.searchPlaceholder}
            value={searchDraft}
            onValue={setSearchDraft}
            resultCount={data ? entries.length : undefined}
            totalCount={data?.buffered}
            countNoun={adminCopy.logs.countNoun}
            className={styles.search}
          />
        </Inline>
        {isError && data && (
          <RefreshFailed
            message={adminCopy.refreshFailed.logs}
            onRetry={() => void refetch()}
          />
        )}
        <div aria-busy={isLoading || undefined}>{body}</div>
      </Stack>
    </section>
  )
}
